import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import PrivacyPolicy from './PrivacyPolicy';
import IntroMedia from './IntroMedia';
import { 
  Camera, 
  Check,
  Handshake,
  Heart,
  Mic, 
  Calendar, 
  TrendingUp, 
  Shield, 
  Scale,
  X
} from 'lucide-react';

function ScrollToTop() {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function HomePage() {
  return (
    <div className="app">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-glow"></div>
        <div className="hero-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_healthtracker-199/artifacts/dyymef0l_icon.webp" 
            alt="Calorie Tracker" 
            className="app-icon"
          />
          <h1 className="app-title">Calorie Tracker</h1>
          <p className="app-tagline">{"Track your calories effortlessly with AI\u2011powered\u00A0tools."}</p>
          
          <a 
            href="https://apps.apple.com/app/id6757595127" 
            className="app-store-badge"
            data-testid="app-store-button"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_healthtracker-199/artifacts/iufyqmzf_Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg" 
              alt="Download on the App Store"
              className="app-store-img"
            />
          </a>
          
          <div className="badges">
            <span className="badge" data-testid="badge-privacy">PRIVACY FIRST</span>
            <span className="badge-separator">·</span>
            <span className="badge" data-testid="badge-ai">AI-POWERED</span>
            <span className="badge-separator">·</span>
            <span className="badge" data-testid="badge-local">100% LOCAL DATA</span>
          </div>
        </div>
        
      </section>

      {/* Intro Media */}
      <section className="intro" data-testid="intro-section">
        <div className="intro-card" data-testid="intro-card">
          <IntroMedia />
        </div>
      </section>

      {/* Philosophy / Hook */}
      <section className="philosophy" data-testid="philosophy-section">
        <div className="philosophy-content">
          <h2 className="philosophy-title">
            More Than Just a <span className="philosophy-accent">Calculator</span>.
          </h2>
          <p className="philosophy-subtitle">Build healthier habits without the obsession.</p>
          <p className="philosophy-description">
            Traditional apps focus purely on the math, making diet tracking feel like a chore. Our platform is a holistic companion that helps you stay mindful, track your real transformation, and seamlessly connect with professionals.
          </p>
        </div>

        <div className="philosophy-cards" aria-label="Key benefits">
          <div className="philosophy-card">
            <div className="philosophy-card-title">
              <TrendingUp className="philosophy-card-icon" aria-hidden="true" />
              Accelerate Your Progress
            </div>
            <p className="philosophy-card-text">
              Log meals, track body measurements, and add photos in one place. Treat it as your personal diary to stay motivated and see real weight-loss results faster.
            </p>
          </div>
          <div className="philosophy-card">
            <div className="philosophy-card-title">
              <Heart className="philosophy-card-icon" aria-hidden="true" />
              Stress-Free Tracking
            </div>
            <p className="philosophy-card-text">
              Don't obsess over exact numbers. Quickly log meals to stay mindful, keep a healthy limit, and build positive eating habits without the anxiety of traditional calorie counting.
            </p>
          </div>
          <div className="philosophy-card">
            <div className="philosophy-card-title">
              <Handshake className="philosophy-card-icon" aria-hidden="true" />
              Partner With Your Coach
            </div>
            <p className="philosophy-card-text">
              Soon, you'll be able to export your weekly stats and progress reports to seamlessly share them with your dietician or personal trainer for professional guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="features-content">
          <h2 className="philosophy-title">
            How the <span className="philosophy-accent">Magic Happens</span>.
          </h2>
          <p className="philosophy-subtitle">
            Everything you need to reach your goals, effortlessly built into your pocket.
          </p>
        </div>
        <div className="features-grid">
          {/* AI Label Scanning */}
          <div className="feature-card feature-card-highlight" data-testid="feature-scan">
            <div className="feature-icon">
              <Camera />
            </div>
            <h3 className="feature-title">
              AI Label Scanning
              <span className="ai-badge">AI</span>
            </h3>
            <p className="feature-description">
              Scan food labels with your camera. AI extracts calories and portions automatically.
            </p>
            <p className="feature-subtext">
              Just point, scan, and track. No manual input needed.
            </p>
          </div>

          {/* Voice Input */}
          <div className="feature-card feature-card-highlight" data-testid="feature-voice">
            <div className="feature-icon">
              <Mic />
            </div>
            <h3 className="feature-title">
              Voice Input
              <span className="ai-badge">AI</span>
            </h3>
            <p className="feature-description">
              Just say what you ate. AI transcribes and calculates calories for you.
            </p>
            <p className="feature-subtext">
              "I had two toasts with ham and cheese" — done!
            </p>
          </div>

          {/* Calendar View */}
          <div className="feature-card feature-card-highlight" data-testid="feature-calendar">
            <div className="feature-icon">
              <Calendar />
            </div>
            <h3 className="feature-title">Calendar View</h3>
            <p className="feature-description">
              See all your meals in a clear timeline. Track daily progress against your calorie goal.
            </p>
            <p className="feature-subtext">
              Visual daily progress bar shows how you're doing.
            </p>
          </div>

          {/* Body Measurements */}
          <div className="feature-card feature-card-highlight" data-testid="feature-measurements">
            <div className="feature-icon">
              <Scale />
            </div>
            <h3 className="feature-title">Body Measurements</h3>
            <p className="feature-description">
              Track weight and body measurements. Add progress photos to see your transformation.
            </p>
            <p className="feature-subtext">
              Weekly check-ins prevent obsessive tracking.
            </p>
          </div>

          {/* Progress Tracking */}
          <div className="feature-card feature-card-highlight" data-testid="feature-progress">
            <div className="feature-icon">
              <TrendingUp />
            </div>
            <h3 className="feature-title">Progress & Insights</h3>
            <p className="feature-description">
              Beautiful charts show your trends. Get weekly motivation and educational insights.
            </p>
            <p className="feature-subtext">
              Stay motivated with smart notifications.
            </p>
          </div>

          {/* Privacy First */}
          <div className="feature-card feature-card-highlight" data-testid="feature-privacy">
            <div className="feature-icon">
              <Shield />
            </div>
            <h3 className="feature-title">Privacy First</h3>
            <p className="feature-description">
              All data stays on your device. No accounts, no cloud storage, no data collection.
            </p>
            <p className="feature-subtext">
              Your health data is yours alone.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="compare" data-testid="compare-section">
        <div className="compare-content">
          <h2 className="philosophy-title">
            Why It <span className="philosophy-accent">Works</span>
          </h2>
          <p className="philosophy-subtitle">Stop wasting time on clunky calorie trackers.</p>
        </div>

        <div className="compare-grid" aria-label="Comparison">
          <div className="compare-column compare-column-negative">
            <h3 className="compare-title">
              Standard Calorie Apps
            </h3>
            <ul className="compare-list">
              <li className="compare-item">
                <X className="compare-item-icon compare-item-icon-negative" aria-hidden="true" />
                Endless scrolling through food databases
              </li>
              <li className="compare-item">
                <X className="compare-item-icon compare-item-icon-negative" aria-hidden="true" />
                Confusing portion sizes and manual math
              </li>
              <li className="compare-item">
                <X className="compare-item-icon compare-item-icon-negative" aria-hidden="true" />
                Requires account creation and email
              </li>
              <li className="compare-item">
                <X className="compare-item-icon compare-item-icon-negative" aria-hidden="true" />
                Annoying ads and paywalls for basic features
              </li>
            </ul>
          </div>

          <div className="compare-column compare-column-positive">
            <h3 className="compare-title">
              Calorie Tracker AI
            </h3>
            <ul className="compare-list">
              <li className="compare-item">
                <Check className="compare-item-icon compare-item-icon-positive" aria-hidden="true" />
                Instantly log meals with camera or voice
              </li>
              <li className="compare-item">
                <Check className="compare-item-icon compare-item-icon-positive" aria-hidden="true" />
                Add meals by scanning nutrition labels
              </li>
              <li className="compare-item">
                <Check className="compare-item-icon compare-item-icon-positive" aria-hidden="true" />
                AI automatically extracts calories
              </li>
              <li className="compare-item">
                <Check className="compare-item-icon compare-item-icon-positive" aria-hidden="true" />
                AI generates meal titles and quick summaries automatically
              </li>
              <li className="compare-item">
                <Check className="compare-item-icon compare-item-icon-positive" aria-hidden="true" />
                Zero setup needed, fully private on-device data
              </li>
              <li className="compare-item">
                <Check className="compare-item-icon compare-item-icon-positive" aria-hidden="true" />
                {'No ads — transparent subscription after the 7\u2011day\u00A0free\u00A0trial'}
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <h2 className="cta-title">Available for iPhone</h2>
        <a 
          href="https://apps.apple.com/app/id6757595127" 
          className="app-store-badge cta-badge"
          data-testid="app-store-button-cta"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_healthtracker-199/artifacts/iufyqmzf_Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg" 
            alt="Download on the App Store"
            className="app-store-img"
          />
        </a>
      </section>

      {/* Footer */}
      <footer className="footer">
        <Link to="/privacy" className="footer-link" data-testid="privacy-policy-link">Privacy Policy</Link>
        <p className="footer-copyright">
          © 2026 Calorie Tracker · <a href="mailto:support@smartkcal.app" className="footer-link">support@smartkcal.app</a>
        </p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
